import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import { EMPTY_LIST, EMPTY_OBJECT } from '../../../constants/common'
import { useContentEdgesShadow } from '../../../hooks'
import Button, { BUTTON_HEIGHT_24, ButtonArrow } from '../../Button'
import { ICON_HEIGHT_16, IconWithTooltip } from '../../Icon'
import { LoaderListItem } from '../../ListItem'
import { ScrollContainer } from '../../ScrollContainer'
import Text from '../../Text'

import { WIDGET_CONTENT_ITEMS_LIST_VISIBLE_COUNT } from './WidgetContentItemsList.constants'
import { getWidgetContentItemPropsFromValue } from './WidgetContentItemsList.utils'

import * as styles from './WidgetContentItemsList.module.css'

import InformationIcon from '../../../icons/info.svg'

function WidgetContentItemsList({
  Item,
  Title = Text,
  className = styles.WidgetContentItemsList,
  emptyStateAction,
  emptyStateClassName,
  emptyStateText,
  getItemPropsFromValue = getWidgetContentItemPropsFromValue,
  getThresholdItemProps,
  info,
  itemProps,
  items = EMPTY_LIST,
  itemsContainerProps: {
    className: itemsContainerClassName = styles.itemsContainer,
  } = EMPTY_OBJECT,
  loadingMore,
  moreButtonTextKey = 'Common.ListShowAllBtnKey',
  title,
  titleEndContent,
  titleProps: {
    className: titleClassName = styles.title,
    isSecondary: titleIsSecondary = true,
    ...titleProps
  } = EMPTY_OBJECT,
  titleWrapperClassName = styles.titleWrapper,
  visibleCount = WIDGET_CONTENT_ITEMS_LIST_VISIBLE_COUNT,
}) {
  const { t } = useTranslation()

  const {
    bottomEndIntersectionElement,
    shadowElement,
    topEndIntersectionElement,
  } = useContentEdgesShadow({ shadowClassName: styles.shadow })

  const [expanded, setExpanded] = useState(false)

  const listItems =
    items?.length > 0 ? items.slice(0, visibleCount) : EMPTY_LIST

  const moreCount = Math.max(items?.length - listItems?.length, 0)
  const isAllVisible = moreCount === 0

  function handleToggleExpanded() {
    setExpanded(prevExpanded => !prevExpanded)
  }

  const visibleItems = expanded ? items : listItems

  const isEmpty = items?.length < 1

  const showEmptyStateAction =
    isEmpty && Boolean(emptyStateAction) && !loadingMore

  return (
    <div className={className}>
      <div className={titleWrapperClassName}>
        {Title && title ? (
          <Title
            {...titleProps}
            className={titleClassName}
            isSecondary={titleIsSecondary}
          >
            {title}
          </Title>
        ) : null}
        {info ? (
          <IconWithTooltip
            Icon={InformationIcon}
            className={styles.infoIcon}
            size={ICON_HEIGHT_16}
            svgSize={ICON_HEIGHT_16}
            tooltip={info}
          />
        ) : null}
        {titleEndContent ? (
          <div className={styles.titleEndContent}>{titleEndContent}</div>
        ) : null}
      </div>
      <div className={styles.contentWrapper}>
        <ScrollContainer className={styles.scrollContainer}>
          <div
            className={classNames(itemsContainerClassName, {
              [styles.itemsContainerLoadingMore]: loadingMore,
            })}
          >
            {topEndIntersectionElement}
            {bottomEndIntersectionElement}
            {visibleItems.map((item, index) => (
              <div
                {...(expanded ? getThresholdItemProps?.(index) : EMPTY_OBJECT)}
                key={item.id || index}
              >
                <Item {...itemProps} {...getItemPropsFromValue(item)} />
              </div>
            ))}
            {showEmptyStateAction ? (
              <div
                className={classNames(styles.emptyState, emptyStateClassName)}
              >
                <Text disabled isSecondary>
                  {emptyStateText}
                </Text>
                {emptyStateAction}
              </div>
            ) : isEmpty ? (
              <Text disabled isSecondary className={emptyStateClassName}>
                {emptyStateText}
              </Text>
            ) : null}
            {loadingMore ? (
              <LoaderListItem
                contentContainerProps={{ className: styles.loader }}
              />
            ) : null}
          </div>
        </ScrollContainer>
        {shadowElement}
      </div>

      {isAllVisible ? null : (
        <div className={styles.footer}>
          <ButtonArrow
            isGrey
            Button={Button.Contained}
            height={BUTTON_HEIGHT_24}
            isOpen={expanded}
            text={t(expanded ? 'Common.ListShowLessBtnKey' : moreButtonTextKey)}
            type='button'
            onClick={handleToggleExpanded}
          />
        </div>
      )}
    </div>
  )
}

export default WidgetContentItemsList
